<template>
  <div class="jr-file-upload">
    <div class="uploader" v-loading="loading">
      <el-upload
        v-bind="$attrs"
        v-on="$listeners"
        ref="fileUpload"
        :action="uploadFileUrl"
        :show-file-list="false"
        :multiple="false"
        :on-success="uploadSuccess"
        :on-change="uploadChange"
        :on-error="uploadError"
        :before-upload="beforeUpload"
        :auto-upload="autoUpload"
        name="fileUpload"
      >
        <div class="flex">
          <el-input
            class="flex-1"
            v-model="fileName"
            :readonly="true"
            :placeholder="placeholder"
          >
          </el-input>
          <el-button class="margin-left-10">浏览</el-button>
        </div>
      </el-upload>
    </div>
    <div style="margin-top: 8px;">
      <i class="icon" :class="[curUploadTips.color, curUploadTips.icon]" />
      {{curUploadTips.text}}
    </div>
  </div>
</template>

<script>
/**
 * 组件说明：
 * 【属性】resetFlag 初始化标识，当它的值改变时，文件上传表单就会被重置
 *                   (通常如果组件嵌在 dialog 弹窗中，控制 dialog 弹窗显隐的属性就可以用作 reset-flag 属性，
 *                   这样就能保证每次弹窗显示时，表单都是初始化过的)
 * 【属性】title：组件标题
 * 【属性】uploadFileUrl 文件上传地址
 * 【属性】placeholder 上传控件提示语
 * 【属性】autoUpload 是否自动上传
 * 【属性】acceptFileType  上传文件类型限制，多个不同类型用 "|" 隔开，如："png|jpg"
 * 【属性】isLoading 是否加载成功后才可以再次选择
 * 【事件】success 上传成功之后的回调函数
 * 【事件】cancel 取消
 * */

export default {
  name: 'file-upload',
  props: {
    list: {
      type: Array,
      default: () => ([]),
    },
    title: {
      type: String,
      default: '请选择上传文件',
    },
    uploadFileUrl: {
      type: String,
      default: '',
    },
    placeholder: {
      type: String,
      default: '请选择上传文件',
    },
    autoUpload: {
      type: Boolean,
      default: false,
    },
    resetFlag: {
      type: Boolean,
      default: false,
    },
    acceptFileType: {
      type: String,
      default: '',
    },
    isLoading: {
      type: Boolean,
      default: true,
    },
    fileSize: {
      type: [String, Number],
      default: 20,
    },
    // 文件最大数量, 默认1个
    fileLimit: {
      type: Number,
      default: 999999,
    },
  },
  data() {
    return {
      fileName: '', // 文件名称
      uploadCode: 1, // 上传状态
      errResData: null, // 失败响应
      loading: false, // 加载
      uploadTips: {
        1: {
          color: 'yellow',
          icon: 'el-icon-warning',
          text: '请选择需要上传的文件',
          showLink: false,
        },
        2: {
          color: 'red',
          icon: 'el-icon-circle-close',
          text: '校验失败，文件类型不符合要求',
          showLink: false,
        },
        3: {
          color: 'green',
          icon: 'el-icon-circle-check',
          text: '文件类型校验成功，等待上传',
          showLink: false,
        },
        4: {
          color: 'green',
          icon: 'el-icon-circle-check',
          text: '文件上传成功...',
          showLink: false,
        },
        5: {
          color: 'red',
          icon: 'el-icon-circle-close',
          text: '上传失败，服务器错误',
          showLink: false,
        },
        6: {
          color: 'red',
          icon: 'el-icon-circle-close',
          text: '校验失败，Excel部分内容错误',
          showLink: true,
        },
        7: {
          color: 'red',
          icon: 'el-icon-circle-close',
          text: '文件上传失败',
          showLink: false,
        },
        8: {
          color: 'red',
          icon: 'el-icon-circle-close',
          text: '校验失败，文件大小不符合要求',
          showLink: false,
        },
        10: {
          color: 'yellow',
          icon: 'el-icon-warning',
          text: `最多上传${this.fileLimit}个`,
          showLink: false,
        },
      },
      uploadFilesList: [],
    };
  },
  computed: {
    curUploadTips() {
      return this.uploadTips[this.uploadCode];
    },
    // 是否超出上传数量
    isExced() {
      const fileList = this.list.length + this.uploadFilesList.length;
      return this.fileLimit && fileList > this.fileLimit;
    },
  },
  watch: {
    resetFlag() {
      this.reset();
    },
  },
  methods: {
    reset() {
      this.fileName = '';
      this.uploadCode = 1;
      this.errResData = null;
    },
    // 获取文件名称 文件状态改变时的钩子，添加文件、上传成功和上传失败时都会被调用
    uploadChange(file, fileList) {
      if (file.status === 'ready') {
        // 已上传文件列表如果存在 n 条，移除第一条，实现替换效果
        if (fileList.length === this.fileLimit) {
          fileList.shift();
        }
        this.fileName = file.name;
        this.handleCheckFileType(file);
        this.$emit('change', { file, fileList });
        this.uploadFilesList = fileList;
      }
    },

    // 校验文件
    handleCheckFileType(file) {
      let beforeUploadRes = true;
      const fileName = file.name;
      const acceptTypeArr = this.acceptFileType ? this.acceptFileType.replace(' ', '').toLowerCase().split('|') : []; // 文件拓展名组成的数组
      const fileType = file.name ? file.name.slice(file.name.lastIndexOf('.') + 1).toLowerCase() : '';
      const isAcceptType = acceptTypeArr.includes(fileType); // 文件拓展名是否在拓展名数组里
      const isLtM = file.size / 1024 / 1024 < (this.fileSize ?? 20);
      this.fileName = fileName;
      if (!isAcceptType) {
        // 如果不符合上传文件类型限制
        this.uploadCode = 2;
        this.uploading = false;
        beforeUploadRes = false;
      } else if (!isLtM) {
        // 上传文件的大小限制
        // ElMessage.warning(`上传大小不能超过 ${props.fileSize}MB!`);
        this.uploadCode = 8;
        this.uploading = false;
        beforeUploadRes = false;
      } else if (this.isExced) {
        // 文件数量限制
        this.uploadCode = 10;
        this.uploading = false;
        beforeUploadRes = false;
      } else {
        // 上传成功
        this.uploadCode = 3;
        beforeUploadRes = true;
      }
      return beforeUploadRes;
    },
    // 上传之前
    beforeUpload(file) {
      return this.handleCheckFileType(file);
    },
    // 上传成功
    uploadSuccess(res) {
      this.loading = false;
      // 成功
      if (res.success) {
        this.uploadCode = 4;
        const obj = {
          url: res?.data || '',
          name: this.fileName || '',
        };
        this.$emit('success', obj);
        this.uploading = false;
        this.fileName = '';
      } else {
        // todo 处理 失败
        this.errResData = res;
        this.uploadCode = 7;
        const { message } = res;
        if (message) {
          this.$message({
            type: 'error',
            duration: 5000,
            showClose: true,
            message,
          });
        }
        this.uploading = false;
        this.$emit('error', message);
        this.fileName = '';
      }
    },
    // 上传失败
    uploadError(err) {
      this.uploading = false;
      this.uploadCode = 5;
      this.fileName = '';
      this.$emit('error', err);
    },
  },
};
</script>
